import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ThunkConfig, ErrorField } from "store";
import { setPasswordService } from "services/setupPasswordService";
import {
  ISetUpPasswordRequest,
  ISetUpPasswordResponse,
  ISetUpPasswordResendOTPRequest,
  ISetUpPasswordResendOTPResponse,
} from "models/setupPassword";

export const setupPassword = createAsyncThunk<
  ISetUpPasswordResponse,
  ISetUpPasswordRequest,
  ThunkConfig<ErrorField[]>
>("password/setupPassword", async (data, { rejectWithValue }) => {
  try {
    const response = await setPasswordService.setPassword(data);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});
export const resendOTP = createAsyncThunk<
  ISetUpPasswordResendOTPResponse,
  ISetUpPasswordResendOTPRequest,
  ThunkConfig<ErrorField[]>
>("password/resendOTP", async (data, { rejectWithValue }) => {
  try {
    const response = await setPasswordService.resendOTP(data);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export interface PasswordSchema {
  isSet: boolean;
  isOpenErrorPopup?: boolean;
  errorMessage?: string;
  errorHeader?: string;
  isInvalidOTPCode?: boolean;
  isOpenResultPopup?: boolean;
  resultHeader?: string;
  resultMessage?: string;
}

interface SetPasswordAction {
  payload: {
    isSet: boolean;
  };
}

const initialState: PasswordSchema = {
  isSet: false,
  isOpenErrorPopup: false,
  resultHeader: "",
  resultMessage: "",
};

export const passwordSlice = createSlice({
  name: "password",
  initialState,
  reducers: {
    setPassword: (state, action: SetPasswordAction) => {
      state.isSet = action.payload.isSet;
    },
    setCloseErrorPopup: (state) => {
      state.isOpenErrorPopup = false;
    },
    setCloseResultPopup: (state) => {
      state.isOpenResultPopup = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setupPassword.fulfilled, (state, action) => {
      if (action.payload.isSet) {
        return {
          ...state,
          isSet: true
        };
      }
    });

    builder.addCase(setupPassword.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_NOT_FOUND_ERROR"
        )
      ) {
        return {
          ...state,
          isSet: false,
          isOpenErrorPopup: true,
          errorHeader: "Client not found",
          errorMessage: "Please verify the information provided",
        };
      }
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_ALREADY_REGISTERED_ERROR"
        )
      ) {
        return {
          ...state,
          isSet: false,
          isOpenErrorPopup: true,
          errorHeader: "Client already registered",
          errorMessage: "Please check the existing records",
        };
      }
      if (
        action.payload?.find(
          (error: any) => error.message === "INVALID_CLIENT_OTP_CODE_ERROR"
        )
      ) {
        return {
          ...state,
          isSet: false,
          isInvalidOTPCode: true,
        };
      }
    });
    builder.addCase(resendOTP.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_NOT_FOUND_ERROR"
        )
      ) {
        return {
          ...state,
          isSet: false,
          isOpenErrorPopup: true,
          errorHeader: "Client not found",
          errorMessage: "Please verify the information provided",
        };
      }
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_ALREADY_REGISTERED_ERROR"
        )
      ) {
        return {
          ...state,
          isSet: false,
          isOpenErrorPopup: true,
          errorHeader: "Client already registered",
          errorMessage: "Please check the existing records",
        };
      }
      if (
        action.payload?.find(
          (error: any) =>
            error.message === "CLIENT_OTP_CODE_TOO_MANY_REQUESTS_ERROR"
        )
      ) {
        return {
          ...state,
          isSet: false,
          isOpenErrorPopup: true,
          errorHeader: "Too many requests",
          errorMessage: "Please try again later",
        };
      }
    });
    builder.addCase(resendOTP.fulfilled, (state, action) => {
      if (action.payload.isResent) {
        return {
          ...state,
          isOpenResultPopup: true,
          resultHeader: "Verification code resent",
          resultMessage: "A new verification code has been sent to your email",
        };
      } else if (!action.payload.isResent) {
        return {
          ...state,
          isOpenResultPopup: true,
          resultHeader: "Error",
          resultMessage:
            "An error occurred while resending the verification code",
        };
      }
    });
  },
});

export const { actions: passwordActions, reducer: passwordReducer } =
  passwordSlice;
